// components/Survey/TakeSurveyPage.jsx — staff answer (or review) one shared survey
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@fluentui/react-components";
import { ArrowLeft24Regular, CheckmarkCircle20Filled, LockClosed24Regular, Info20Regular } from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useSurveys } from "../../context/SurveyContext";
import { C, EmptyState, LoadingBlock, ErrorBanner } from "./surveyUI";
import { formatDateTime } from "./surveyUtils";
import SurveyRunner from "./SurveyRunner";

function Banner({ tone, icon: Icon, children }) {
  const t = {
    good: { bg: "#E7F4E4", border: "#BFE0B8", color: "#0B5A0B" },
    muted: { bg: "#F3F2F1", border: "#E1DFDD", color: C.ink2 },
    info: { bg: "#F0F6FF", border: "#C7DBFF", color: "#1B3A6B" },
  }[tone];
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color, borderRadius: 10, padding: "12px 16px", fontSize: 13, lineHeight: 1.6, marginBottom: 14, display: "flex", gap: 10, alignItems: "flex-start" }}>
      <Icon style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

export default function TakeSurveyPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { currentUserEmail } = useUserAccess();
  const { myAssignments, loading, error, refresh, submitResponse } = useSurveys();
  const a = myAssignments.find((x) => x.id === assignmentId);
  // Keeps the runner (and its thank-you screen) mounted once this page submits
  const [submittedHere, setSubmittedHere] = useState(false);

  const back = (
    <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate("/my-surveys")} style={{ marginBottom: 12 }}>
      My surveys
    </Button>
  );

  if (loading) return <LoadingBlock label="Loading survey…" />;
  if (error) return <div style={{ maxWidth: 760, margin: "0 auto" }}>{back}<ErrorBanner error={error} onRetry={refresh} /></div>;
  if (!a) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {back}
        <EmptyState
          icon={LockClosed24Regular}
          title="This survey isn't available"
          text={`It may have been withdrawn, or it was shared with a different account. You are signed in as ${currentUserEmail}.`}
          action={<Button appearance="primary" onClick={() => navigate("/my-surveys")}>Go to my surveys</Button>}
        />
      </div>
    );
  }

  // A newer, still-open share of the same survey supersedes this one
  const newer = myAssignments.find((x) => x.surveyId === a.surveyId && x.id !== a.id && x.round > a.round && (x.status === "pending" || x.status === "overdue"));

  if (a.status === "submitted" && !submittedHere) {
    return (
      <div>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>{back}</div>
        <SurveyRunner
          survey={a.survey}
          mode="review"
          initialAnswers={a.response.answers}
          notice={
            <Banner tone="good" icon={CheckmarkCircle20Filled}>
              <strong>You submitted this survey on {formatDateTime(a.response.submittedAt)}.</strong> Your answers are shown below and can no longer be changed.
              {newer
                ? <div style={{ marginTop: 6 }}>This survey has been shared with you again. <Button size="small" appearance="primary" onClick={() => navigate(`/my-surveys/${newer.id}`)} style={{ marginLeft: 6 }}>Respond again</Button></div>
                : <div style={{ marginTop: 2 }}>You'll be able to respond again if the survey is shared with you again.</div>}
            </Banner>
          }
        />
      </div>
    );
  }

  if (a.status === "expired" && !submittedHere) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {back}
        <EmptyState icon={LockClosed24Regular} title={a.survey.title} text="This survey is closed and no longer accepting responses. Thank you for your interest." />
      </div>
    );
  }

  return (
    <div>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>{back}</div>
      <SurveyRunner
        survey={a.survey}
        mode="take"
        dueDate={a.dueDate}
        draftKey={`lekoil_survey_draft_${a.id}`}
        onSubmit={async (answers, duration) => {
          setSubmittedHere(true);
          try {
            await submitResponse(a.id, answers, duration);
          } catch (err) {
            setSubmittedHere(false);
            throw err;
          }
        }}
        notice={a.round > 1 ? (
          <Banner tone="info" icon={Info20Regular}>This survey was shared with you again (round {a.round}). Please answer based on how things are now.</Banner>
        ) : null}
        footer={<Button appearance="primary" onClick={() => navigate("/my-surveys")}>Back to my surveys</Button>}
      />
    </div>
  );
}
